import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Trash2, Building2, Users, Calendar, Loader2,
  Send, Clock, Phone, Mail, MessageSquare, FileText, Plus,
  Briefcase, ArrowRight, ExternalLink, Link2, Unlink
} from "lucide-react";
import {
  DEAL_STATUSES, DEAL_STATUS_LABELS, DEAL_STATUS_COLORS,
  PRIORITIES, PRIORITY_LABELS, ACTIVITY_TYPES
} from "../lib/constants";

const ACTIVITY_ICONS = { note: MessageSquare, call: Phone, email: Mail, meeting: Users, status_change: ArrowRight, proposal: FileText };
const ACTIVITY_COLORS = { note: "text-blue-400", call: "text-green-400", email: "text-cyan-400", meeting: "text-purple-400", status_change: "text-amber-400", proposal: "text-pink-400" };

export default function DealDetailPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [activities, setActivities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [activityForm, setActivityForm] = useState({ type: "note", title: "", description: "" });
  const [addingActivity, setAddingActivity] = useState(false);
  const [creatingProposal, setCreatingProposal] = useState(false);
  const [showLinkProposal, setShowLinkProposal] = useState(false);

  useEffect(() => { fetchData(); }, [id]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [dRes, aRes, cRes, coRes, pRes] = await Promise.all([
        axios.get(`${API}/deals/${id}`, { headers }),
        axios.get(`${API}/deals/${id}/activities`, { headers }),
        axios.get(`${API}/companies`, { headers }),
        axios.get(`${API}/contacts`, { headers }),
        axios.get(`${API}/proposals`, { headers })
      ]);
      setDeal(dRes.data);
      setForm(dRes.data);
      setActivities(aRes.data);
      setCompanies(cRes.data);
      setContacts(coRes.data);
      setProposals(pRes.data);
    } catch { toast.error("Failed to load deal"); navigate("/deals"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { title: form.title, company_id: form.company_id || null, primary_contact_id: form.primary_contact_id || null, status: form.status, value_estimate: parseFloat(form.value_estimate) || 0, priority: form.priority, source: form.source, next_action_date: form.next_action_date || null, notes: form.notes };
      const res = await axios.put(`${API}/deals/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setDeal(res.data);
      setEditing(false);
      toast.success("Deal updated");
      const aRes = await axios.get(`${API}/deals/${id}/activities`, { headers: { Authorization: `Bearer ${token}` } });
      setActivities(aRes.data);
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this deal and all its activities?")) return;
    try {
      await axios.delete(`${API}/deals/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deal deleted");
      navigate("/deals");
    } catch { toast.error("Failed to delete"); }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!activityForm.title) return;
    setAddingActivity(true);
    try {
      await axios.post(`${API}/deals/${id}/activities`, activityForm, { headers: { Authorization: `Bearer ${token}` } });
      setActivityForm({ type: "note", title: "", description: "" });
      const aRes = await axios.get(`${API}/deals/${id}/activities`, { headers: { Authorization: `Bearer ${token}` } });
      setActivities(aRes.data);
      toast.success("Activity added");
    } catch { toast.error("Failed to add activity"); }
    finally { setAddingActivity(false); }
  };

  const handleCreateProposal = async () => {
    setCreatingProposal(true);
    try {
      const res = await axios.post(`${API}/deals/${id}/create-proposal`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Proposal created!");
      navigate(`/proposals/${res.data.id}/edit`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create proposal");
    }
    finally { setCreatingProposal(false); }
  };

  const handleLinkProposal = async (proposalId) => {
    try {
      await axios.put(`${API}/proposals/${proposalId}/link-deal/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Proposal linked!");
      setShowLinkProposal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link proposal");
    }
  };

  const handleUnlinkProposal = async () => {
    if (!deal.proposal_id) return;
    try {
      await axios.put(`${API}/proposals/${deal.proposal_id}/unlink-deal`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Proposal unlinked");
      fetchData();
    } catch { toast.error("Failed to unlink"); }
  };

  const companyContacts = form.company_id ? contacts.filter(c => c.company_id === form.company_id) : contacts;
  const linkedProposal = deal?.proposal_id ? proposals.find(p => p.id === deal.proposal_id) : null;
  const unlinkableProposals = proposals.filter(p => !p.deal_id && p.id !== deal?.proposal_id);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#C9A96E]" /></div>;
  if (!deal) return null;

  const statusIdx = DEAL_STATUSES.indexOf(deal.status);

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/deals" className="p-2 rounded-lg hover:bg-white/5 text-white/40 hover:text-white transition-colors"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="font-display text-2xl text-white" data-testid="deal-detail-title">{deal.title}</h1>
          <p className="text-white/40 text-sm">{deal.company_name || "No company"}{deal.primary_contact_name ? ` · ${deal.primary_contact_name}` : ""}</p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button onClick={() => { setEditing(false); setForm(deal); }} className="px-4 py-2 text-sm text-white/50 hover:text-white bg-white/5 rounded-lg">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn-accent flex items-center gap-2 text-sm" data-testid="save-deal-detail-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} className="px-4 py-2 text-sm text-white bg-white/5 hover:bg-white/10 rounded-lg" data-testid="edit-deal-btn">Edit</button>
              <button onClick={handleDelete} className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg" data-testid="delete-deal-btn"><Trash2 className="w-4 h-4" /></button>
            </>
          )}
        </div>
      </div>

      {/* Status Progress Bar */}
      <div className="glass-card-static p-4 mb-6 overflow-x-auto" data-testid="deal-status-bar">
        <div className="flex gap-1 min-w-max">
          {DEAL_STATUSES.map((s, i) => (
            <div key={s} className={`flex-1 min-w-[80px] text-center py-1.5 px-2 rounded-lg text-[10px] font-medium uppercase tracking-wider transition-colors ${i <= statusIdx ? (s === "closed_won" ? "bg-green-500/20 text-green-400" : s === "closed_lost" ? "bg-red-500/20 text-red-400" : "bg-[#C9A96E]/15 text-[#C9A96E]") : "bg-white/[0.03] text-white/20"}`}>
              {DEAL_STATUS_LABELS[s]}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Deal Info */}
          {editing ? (
            <div className="glass-card-static p-6 space-y-4">
              <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2">Deal Details</h3>
              <div><label className="form-label">Title</label><input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="form-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="form-label">Status</label>
                  <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="form-input">
                    {DEAL_STATUSES.map(s => <option key={s} value={s}>{DEAL_STATUS_LABELS[s]}</option>)}
                  </select>
                </div>
                <div><label className="form-label">Priority</label>
                  <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className="form-input">
                    {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div><label className="form-label">Company</label>
                <select value={form.company_id || ""} onChange={e => setForm(p => ({ ...p, company_id: e.target.value }))} className="form-input">
                  <option value="">No Company</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
              <div><label className="form-label">Primary Contact</label>
                <select value={form.primary_contact_id || ""} onChange={e => setForm(p => ({ ...p, primary_contact_id: e.target.value }))} className="form-input">
                  <option value="">No Contact</option>
                  {companyContacts.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="form-label">Value ({"\u20AC"})</label><input type="number" value={form.value_estimate} onChange={e => setForm(p => ({ ...p, value_estimate: e.target.value }))} className="form-input" /></div>
                <div><label className="form-label">Next Action</label><input type="date" value={form.next_action_date || ""} onChange={e => setForm(p => ({ ...p, next_action_date: e.target.value }))} className="form-input" /></div>
              </div>
              <div><label className="form-label">Source</label><input value={form.source || ""} onChange={e => setForm(p => ({ ...p, source: e.target.value }))} className="form-input" /></div>
              <div><label className="form-label">Notes</label><textarea value={form.notes || ""} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="form-input min-h-[100px]" /></div>
            </div>
          ) : (
            <div className="glass-card-static p-6">
              {deal.notes && <p className="text-white/60 text-sm whitespace-pre-wrap mb-4">{deal.notes}</p>}
              <div className="flex flex-wrap gap-4 text-sm">
                {deal.company_name && (
                  <Link to={`/companies/${deal.company_id}`} className="flex items-center gap-2 text-white/60 hover:text-[#C9A96E] transition-colors">
                    <Building2 className="w-4 h-4" />{deal.company_name}
                  </Link>
                )}
                {deal.primary_contact_name && (
                  <Link to={`/contacts/${deal.primary_contact_id}`} className="flex items-center gap-2 text-white/60 hover:text-[#C9A96E] transition-colors">
                    <Users className="w-4 h-4" />{deal.primary_contact_name}
                  </Link>
                )}
                {deal.next_action_date && (
                  <span className="flex items-center gap-2 text-white/60"><Calendar className="w-4 h-4" />Next: {new Date(deal.next_action_date).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          )}

          {/* Linked Proposal - REAL INTEGRATION */}
          <div className="glass-card-static p-6" data-testid="deal-proposal-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white/50 text-xs uppercase tracking-wider">Linked Proposal</h3>
              {!linkedProposal && (
                <div className="flex gap-2">
                  <button onClick={handleCreateProposal} disabled={creatingProposal} className="btn-accent text-xs flex items-center gap-1.5" data-testid="create-proposal-from-deal-btn">
                    {creatingProposal ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Create Proposal
                  </button>
                  <button onClick={() => setShowLinkProposal(!showLinkProposal)} className="px-3 py-1.5 text-xs text-white/50 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg flex items-center gap-1.5" data-testid="link-existing-proposal-btn">
                    <Link2 className="w-3 h-3" /> Link Existing
                  </button>
                </div>
              )}
            </div>

            {linkedProposal ? (
              <div className="p-4 bg-white/[0.03] rounded-xl border border-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#C9A96E]/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#C9A96E]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{linkedProposal.hotel_name}</p>
                      <p className="text-white/40 text-xs">ID: {linkedProposal.proposal_id} · Status: {linkedProposal.status}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/proposals/${linkedProposal.id}/edit`} className="px-3 py-1.5 text-xs text-[#C9A96E] hover:bg-[#C9A96E]/10 rounded-lg flex items-center gap-1.5 transition-colors" data-testid="open-linked-proposal-btn">
                      <ExternalLink className="w-3 h-3" /> Open
                    </Link>
                    <button onClick={handleUnlinkProposal} className="p-1.5 text-white/30 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors" title="Unlink proposal" data-testid="unlink-proposal-btn">
                      <Unlink className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ) : showLinkProposal ? (
              <div className="space-y-2" data-testid="link-proposal-list">
                {unlinkableProposals.length === 0 ? (
                  <p className="text-white/30 text-sm py-4 text-center">No unlinked proposals available</p>
                ) : (
                  unlinkableProposals.map(p => (
                    <button key={p.id} onClick={() => handleLinkProposal(p.id)} className="w-full p-3 bg-white/[0.03] rounded-lg hover:bg-white/[0.06] transition-colors text-left flex items-center gap-3" data-testid={`link-proposal-${p.id}`}>
                      <FileText className="w-4 h-4 text-white/30 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{p.hotel_name}</p>
                        <p className="text-white/40 text-xs">{p.proposal_id} · {p.status}</p>
                      </div>
                      <Link2 className="w-4 h-4 text-[#C9A96E]" />
                    </button>
                  ))
                )}
              </div>
            ) : (
              <p className="text-white/30 text-sm">No proposal linked yet. Create a new one or link an existing proposal.</p>
            )}
          </div>

          {/* Activity Timeline */}
          <div className="glass-card-static p-6" data-testid="activity-timeline">
            <h3 className="text-white/50 text-xs uppercase tracking-wider mb-4">Activity Timeline</h3>
            <form onSubmit={handleAddActivity} className="mb-6 p-4 bg-white/[0.02] rounded-xl border border-white/5">
              <div className="flex gap-3 mb-3">
                <select value={activityForm.type} onChange={e => setActivityForm(p => ({ ...p, type: e.target.value }))} className="form-input w-auto text-sm" data-testid="activity-type-select">
                  {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
                <input value={activityForm.title} onChange={e => setActivityForm(p => ({ ...p, title: e.target.value }))} placeholder="Activity title..." className="form-input flex-1 text-sm" data-testid="activity-title-input" />
              </div>
              <textarea value={activityForm.description} onChange={e => setActivityForm(p => ({ ...p, description: e.target.value }))} placeholder="Details (optional)..." className="form-input w-full text-sm min-h-[60px] mb-3" data-testid="activity-description-input" />
              <button type="submit" disabled={addingActivity || !activityForm.title} className="btn-accent text-sm flex items-center gap-2" data-testid="add-activity-btn">
                {addingActivity ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add Activity
              </button>
            </form>

            <div className="relative">
              <div className="absolute left-[15px] top-0 bottom-0 w-px bg-white/5" />
              {activities.length === 0 ? (
                <p className="text-white/30 text-sm pl-10 py-4">No activities yet</p>
              ) : (
                <div className="space-y-4">
                  {activities.map(a => {
                    const Icon = ACTIVITY_ICONS[a.type] || MessageSquare;
                    const color = ACTIVITY_COLORS[a.type] || "text-white/40";
                    return (
                      <div key={a.id} className="relative pl-10" data-testid={`activity-${a.id}`}>
                        <div className="absolute left-[7px] top-1 w-[17px] h-[17px] rounded-full bg-[#0A0A0A] border-2 border-white/10 flex items-center justify-center">
                          <Icon className={`w-2.5 h-2.5 ${color}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-white text-sm font-medium">{a.title}</span>
                            <span className="text-white/20 text-xs">{new Date(a.created_at).toLocaleString()}</span>
                          </div>
                          {a.description && <p className="text-white/50 text-sm">{a.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Future: Post-Close Section (Purchase + Fulfillment) */}
          {deal.status === "closed_won" && (
            <div className="glass-card-static p-6 border border-dashed border-green-500/20" data-testid="post-close-section">
              <div className="flex items-center gap-3 text-green-400/60 mb-2">
                <Briefcase className="w-5 h-5" />
                <p className="text-sm font-medium">Post-Close: Purchase & Fulfillment</p>
              </div>
              <p className="text-white/30 text-xs">This deal is closed. Product purchase and fulfillment task management will be available here.</p>
            </div>
          )}

          {/* Future Communication History */}
          <div className="glass-card-static p-6 border border-dashed border-white/10" data-testid="communication-placeholder">
            <div className="flex items-center gap-3 text-white/30">
              <Mail className="w-5 h-5" />
              <div>
                <p className="text-sm font-medium">Communication History</p>
                <p className="text-xs">Email and message history will appear here in a future update.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="glass-card-static p-6">
            <h3 className="text-white/50 text-xs uppercase tracking-wider mb-3">Deal Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-white/40">Status</span><span className={`px-2 py-0.5 rounded text-xs font-medium ${DEAL_STATUS_COLORS[deal.status]}`}>{DEAL_STATUS_LABELS[deal.status]}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Priority</span><span className="text-white/70">{deal.priority?.charAt(0).toUpperCase() + deal.priority?.slice(1)}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Value</span><span className="text-white font-mono">{"\u20AC"}{(deal.value_estimate || 0).toLocaleString()}</span></div>
              {deal.source && <div className="flex justify-between"><span className="text-white/40">Source</span><span className="text-white/70">{deal.source}</span></div>}
              <div className="flex justify-between"><span className="text-white/40">Created</span><span className="text-white/70">{new Date(deal.created_at).toLocaleDateString()}</span></div>
              {deal.next_action_date && <div className="flex justify-between"><span className="text-white/40">Next Action</span><span className="text-white/70">{new Date(deal.next_action_date).toLocaleDateString()}</span></div>}
            </div>
          </div>

          {deal.company_name && (
            <div className="glass-card-static p-6">
              <h3 className="text-white/50 text-xs uppercase tracking-wider mb-3">Company</h3>
              <Link to={`/companies/${deal.company_id}`} className="flex items-center gap-3 p-3 bg-white/[0.03] rounded-lg hover:bg-white/[0.06] transition-colors">
                <Building2 className="w-5 h-5 text-white/30" />
                <span className="text-white text-sm">{deal.company_name}</span>
              </Link>
            </div>
          )}

          {deal.primary_contact_name && (
            <div className="glass-card-static p-6">
              <h3 className="text-white/50 text-xs uppercase tracking-wider mb-3">Primary Contact</h3>
              <Link to={`/contacts/${deal.primary_contact_id}`} className="flex items-center gap-3 p-3 bg-white/[0.03] rounded-lg hover:bg-white/[0.06] transition-colors">
                <Users className="w-5 h-5 text-white/30" />
                <span className="text-white text-sm">{deal.primary_contact_name}</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
